import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { fillTimezoneIfEmpty } from "../lib/syncBrowserTimezone";
import { useAuth } from "../contexts/AuthContext";

const DEFAULT_TASK_DURATION = 30;

export const useUserPreferences = () => {
  const { user } = useAuth();
  const [defaultTaskDuration, setDefaultTaskDuration] =
    useState(DEFAULT_TASK_DURATION);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPreferences = useCallback(async () => {
    if (!user) {
      setDefaultTaskDuration(DEFAULT_TASK_DURATION);
      setTimezone(null);
      setIsLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("user_preferences")
      .select("default_task_duration, timezone")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      console.error("Failed to fetch user preferences:", error);
    }

    setDefaultTaskDuration(data?.default_task_duration ?? DEFAULT_TASK_DURATION);
    setTimezone(data?.timezone ?? null);
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Auto-detect and sync browser timezone to the database.
  // The cron job uses this to compute "today" per user when rescheduling persistent tasks.
  useEffect(() => {
    if (!user || isLoading) return;

    if (!timezone) {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected) setTimezone(detected);
      fillTimezoneIfEmpty(user.id);
    }
  }, [user, isLoading, timezone]);

  const updateDefaultTaskDuration = useCallback(
    async (minutes: number) => {
      if (!user) return;

      // Optimistic update
      setDefaultTaskDuration(minutes);

      const { error } = await supabase.from("user_preferences").upsert(
        {
          user_id: user.id,
          default_task_duration: minutes,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" },
      );

      if (error) {
        console.error("Failed to update default task duration:", error);
        // Revert on failure
        fetchPreferences();
      }
    },
    [user, fetchPreferences],
  );

  const updateTimezone = useCallback(
    async (next: string) => {
      if (!user || !next) return;
      setTimezone(next);
      const { error } = await supabase.from("user_preferences").upsert(
        {
          user_id: user.id,
          timezone: next,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" },
      );
      if (error) {
        console.error("Failed to update timezone:", error);
        fetchPreferences();
      }
    },
    [user, fetchPreferences],
  );

  return {
    defaultTaskDuration,
    timezone,
    isLoading,
    updateDefaultTaskDuration,
    updateTimezone,
  };
};
